// components/AboutFormBuilder/AboutFieldBuilder.jsx
import React from "react";
import {
  Box, Typography, Paper, Chip, IconButton,
  Tooltip
} from "@mui/material";
import {
  Delete as DeleteIcon,
  DragIndicator as DragIndicatorIcon,
  Edit as EditIcon
} from "@mui/icons-material";

const FIELD_TYPE_ICONS = {
  singleline: '📝',
  multiline: '📄',
  richtext: '📝',
  select: '📋',
  number: '🔢',
  date: '📅',
  datetime: '🕐',
  checkbox: '☑️',
  email: '✉️',
  phone: '📞',
  file: '📎'
};

const FIELD_TYPE_LABELS = {
  singleline: 'Single Line',
  multiline: 'Multi Line',
  richtext: 'Rich Text',
  select: 'Dropdown',
  number: 'Number',
  date: 'Date',
  datetime: 'Date & Time',
  checkbox: 'Checkbox',
  email: 'Email',
  phone: 'Phone',
  file: 'File Upload'
};

const AboutFieldBuilder = ({ field, sectionId, fieldIndex, onDelete, onEdit }) => {
  return (
    <Paper
      variant="outlined"
      sx={{
        p: 1.5,
        mb: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        bgcolor: '#ffffff',
        border: '1px solid #e2e8f0',
        borderRadius: '6px',
        '&:hover': {
          bgcolor: '#fafafa',
          borderColor: '#cbd5e1'
        }
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1 }}>
        <DragIndicatorIcon sx={{ color: '#cbd5e1', fontSize: 18, cursor: 'grab' }} />

        <Box sx={{ flex: 1 }}>
          <Typography variant="body2" fontWeight={500} sx={{ color: '#0f172a' }}>
            {field.label || `Field ${fieldIndex + 1}`}
            {field.required && (
              <Typography component="span" sx={{ color: '#ef4444', ml: 0.5 }}>*</Typography>
            )}
          </Typography>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5, flexWrap: 'wrap' }}>
            <Chip
              icon={<span>{FIELD_TYPE_ICONS[field.field_type] || '📝'}</span>}
              label={FIELD_TYPE_LABELS[field.field_type] || field.field_type}
              size="small"
              sx={{
                height: 20,
                fontSize: '0.65rem',
                bgcolor: '#f1f5f9',
                '& .MuiChip-label': { px: 1, fontSize: '0.65rem' }
              }}
            />

            {field.required && (
              <Chip
                label="Required"
                size="small"
                sx={{
                  height: 20,
                  fontSize: '0.65rem',
                  bgcolor: '#fef2f2',
                  color: '#dc2626',
                  '& .MuiChip-label': { px: 1, fontSize: '0.65rem' }
                }}
              />
            )}

            {field.field_type === 'select' && field.choices && (
              <Chip
                label={`${field.choices.split(',').length} options`}
                size="small"
                sx={{
                  height: 20,
                  fontSize: '0.65rem',
                  bgcolor: '#eff6ff',
                  color: '#2563eb',
                  '& .MuiChip-label': { px: 1, fontSize: '0.65rem' }
                }}
              />
            )}

            {field.placeholder && (
              <Typography variant="caption" color="textSecondary" sx={{ fontSize: '0.6rem' }}>
                Placeholder: {field.placeholder}
              </Typography>
            )}
          </Box>

          {field.help_text && (
            <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 0.5, fontSize: '0.65rem' }}>
              {field.help_text}
            </Typography>
          )}
        </Box>
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Tooltip title="Edit Field">
          <IconButton
            size="small"
            onClick={() => onEdit(field)}
            sx={{
              border: '1px solid #e2e8f0',
              borderRadius: 1,
              padding: '3px',
              '&:hover': {
                bgcolor: '#f1f5f9'
              }
            }}
          >
            <EditIcon fontSize="small" sx={{ fontSize: '14px' }} />
          </IconButton>
        </Tooltip>
        <Tooltip title="Delete Field">
          <IconButton
            size="small"
            onClick={() => onDelete(field.id, field.label)}
            sx={{
              border: '1px solid #fee2e2',
              borderRadius: 1,
              padding: '3px',
              '&:hover': {
                bgcolor: '#fef2f2'
              }
            }}
          >
            <DeleteIcon fontSize="small" sx={{ color: '#ef4444', fontSize: '14px' }} />
          </IconButton>
        </Tooltip>
      </Box>
    </Paper>
  );
};

export default AboutFieldBuilder;